import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import DesignDiscountDialog from "../components/DesignDiscountDialog";

const DesignDiscounts = () => {
  const toast = useRef(null);
  const [data, setData] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [globalFilter, setGlobalFilter] = useState(null);
  const [height, setHeight] = useState(0);
  const dt = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const updateHeight = () => {
      setHeight(Math.max(200, window.innerHeight - 320));
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get("/api/designDiscounts");
      if (Array.isArray(res.data)) setData(res.data);
    } catch {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load design discounts.",
        life: 3000,
      });
    }
  };

  const editDiscount = (rowData) => {
    setSelected(rowData || null);
    setDialogVisible(true);
  };

  const hideDialog = () => {
    setDialogVisible(false);
    setSelected(null);
  };

  const handleDelete = (rowData) => {
    confirmDialog({
      message: "Remove this design discount?",
      header: "Confirm",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        try {
          await axios.delete(`/api/designDiscounts/${rowData.id}`);
          toast.current.show({
            severity: "success",
            summary: "Deleted",
            life: 3000,
          });
          fetchData();
        } catch {
          toast.current.show({
            severity: "error",
            summary: "Delete failed",
            life: 3000,
          });
        }
      },
    });
  };

  const typeTemplate = (rowData) =>
    rowData.isPercentage ? "Percentage" : "Fixed (LKR)";
  const valueTemplate = (rowData) =>
    rowData.isPercentage ? `${rowData.value}%` : `LKR ${rowData.value}`;
  const discountColumnBody = (rowData) => (
    <span className="font-semibold">{valueTemplate(rowData)}</span>
  );
  const actionBody = (rowData) => (
    <>
      <Button
        icon="pi pi-pencil"
        className="p-button-rounded p-button-info mr-2"
        tooltip="Edit"
        onClick={() => editDiscount(rowData)}
      />
      <Button
        icon="pi pi-trash"
        className="p-button-rounded p-button-danger"
        tooltip="Delete"
        onClick={() => handleDelete(rowData)}
      />
    </>
  );

  return (
    <div className="card flex flex-column gap-4">
      <Toast ref={toast} position="bottom-right" />
      <div className="flex justify-content-between align-items-center">
        <div>
          <h4 className="text-primary mb-1">Design (Item) Discounts</h4>
          <label className="text-secondary">
            Set discount per gallery design. Shown as a bubble on the item card and applied in cart.
          </label>
        </div>
        <div className="flex gap-2 align-items-center">
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              type="search"
              placeholder="Search"
              onInput={(e) => setGlobalFilter(e.target.value)}
              className="p-inputtext-sm"
            />
          </span>
          <Button label="Refresh" icon="pi pi-refresh" onClick={fetchData} />
          <Button
            label="Add discount"
            icon="pi pi-plus"
            className="p-button-primary"
            onClick={() => editDiscount(null)}
          />
        </div>
      </div>
      <Dialog
        visible={dialogVisible}
        onHide={hideDialog}
        header="Add / Edit design discount"
        style={{ width: "420px" }}
      >
        <DesignDiscountDialog
          discount={selected}
          onCancel={hideDialog}
          onSubmit={() => {
            hideDialog();
            fetchData();
          }}
        />
      </Dialog>
      <DataTable
        ref={dt}
        value={data}
        paginator
        rows={25}
        rowsPerPageOptions={[25, 50, 100]}
        globalFilter={globalFilter}
        scrollable
        scrollHeight={height}
        className="p-datatable-striped"
      >
        <Column header="" body={actionBody} style={{ width: "120px" }} />
        <Column field="designId" header="Design ID" sortable />
        <Column field="designName" header="Design name" sortable />
        <Column header="Discount" body={discountColumnBody} sortable sortField="value" />
        <Column header="Type" body={typeTemplate} sortable />
        <Column header="Value" body={valueTemplate} sortable />
      </DataTable>
    </div>
  );
};

export default DesignDiscounts;
